import AdminLogin from '../components/AdminLogin';
// src/pages/News.jsx
import React, { useState, useEffect } from 'react';
import { Upload, Lock, X, LogIn, ArrowRight, Calendar, FileText, Clock, Image,
    Trash2, ChevronDown, ChevronUp, Link2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { articleUtils } from '../lib/contentUtils';
import { imageUtils } from "../utils/imageUtils";
import { eventUtils } from '../lib/eventUtils';

import ArticleOverlay from "../components/ArticleOverlay";
import { extractLinks, getPreviewText } from '../utils/linkUtils';


const EXCLUDED_CATEGORIES = ['all', 'Events', 'Education', 'Health', 'Community', 'Projects'];


// Article Card Component
const NewsArticle = ({ article, isAdmin, onDelete, onClick }) => {
    const imageUrl = article.imageUrl;
    const linkCount = extractLinks(article.content).length;
    const preview = getPreviewText(article.content) || article.content;

    const handleClick = (e) => {
        if (e.target.closest('button[data-delete]')) return;
        onClick(article);
    };

    return (
        <article
            onClick={handleClick}
            className="group bg-white rounded-2xl border border-gray-100 overflow-hidden
                     shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-purple-100
                     transition-all duration-300 cursor-pointer h-full flex flex-col"
        >
            {/* Media */}
            <div className="relative w-full h-52 overflow-hidden bg-gradient-to-br from-purple-100 via-purple-50 to-white">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out
                                 group-hover:scale-105"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center">
                        <FileText className="h-12 w-12 text-purple-300" />
                    </div>
                )}
                {/* Legibility gradient + category chip */}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute left-4 top-4 rounded-full bg-white/90 backdrop-blur px-3 py-1
                               text-xs font-semibold text-purple-700 shadow-sm">
                    News
                </span>
                {isAdmin && (
                    <button
                        data-delete
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(article.$id, article.imageId);
                        }}
                        aria-label="Delete article"
                        className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full
                                 bg-white/90 text-red-500 shadow-sm hover:bg-red-500 hover:text-white
                                 transition-colors"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>

            <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center">
                        <Calendar className="w-3.5 h-3.5 mr-1.5" />
                        {article.dateLabel || new Date(article.createdAt).toLocaleDateString('en-US', { timeZone: 'America/Antigua' })}
                    </div>
                    <div className="flex items-center">
                        <Clock className="w-3.5 h-3.5 mr-1.5" />
                        5 min read
                    </div>
                    {linkCount > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5
                                       font-semibold text-purple-600">
                            <Link2 className="w-3.5 h-3.5" />
                            {linkCount} link{linkCount > 1 ? 's' : ''}
                        </span>
                    )}
                </div>

                <h3 className="font-display text-xl font-semibold text-gray-900 mb-2 leading-snug
                             group-hover:text-purple-700 transition-colors duration-300">
                    {article.title}
                </h3>

                <p className="text-gray-600 text-sm leading-relaxed mb-5 line-clamp-3 flex-1">
                    {preview}
                </p>

                <div className="flex items-center text-sm font-semibold text-purple-600 mt-auto">
                    {linkCount > 0 ? 'Read & open links' : 'Continue Reading'}
                    <ArrowRight className="w-4 h-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
            </div>
        </article>
    );
};

// Collapsible Admin Panel Component
const AdminPanel = ({ isAdmin, onLogout, onSubmit }) => {
    const [isExpanded, setIsExpanded] = useState(false);




    if (!isAdmin) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg transform transition-transform duration-300 z-50">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="absolute -top-12 right-6 bg-white rounded-t-lg px-4 py-2 border border-b-0 border-gray-200
                         flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900
                         transition-colors shadow-sm"
            >
                Admin Panel
                {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                ) : (
                    <ChevronUp className="w-4 h-4" />
                )}
            </button>

            <div className={`max-h-${isExpanded ? '120' : '0'} overflow-hidden transition-all duration-300`}>
                <div className="max-w-5xl mx-auto p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Admin Dashboard</h2>
                        <div className="flex gap-2">
                            <button
                                onClick={onLogout}
                                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700
                                         transition-colors"
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                    <AdminUploadForm onSubmit={onSubmit} />
                </div>
            </div>
        </div>
    );
};

// Continuation of News.jsx

const AdminUploadForm = ({ onSubmit }) => {
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, image: file }));
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            if (!formData.title.trim()) throw new Error('Title is required');
            if (!formData.content.trim()) throw new Error('Content is required');

            await onSubmit(formData);

            // Reset form
            setFormData({ title: '', content: '', image: null });
            setImagePreview(null);
            alert('Article posted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(error.message || 'Failed to post article');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <input
                        type="text"
                        placeholder="Article Title"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg
                                 focus:ring-2 focus:ring-purple-100 focus:border-purple-400
                                 transition duration-200"
                        required
                    />

                    <textarea
                        placeholder="Article Content"
                        value={formData.content}
                        onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                        rows={12}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-lg
                                 focus:ring-2 focus:ring-purple-100 focus:border-purple-400
                                 transition duration-200"
                        required
                    />

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full inline-flex items-center justify-center gap-2 px-6 py-2.5
                                 bg-purple-600 hover:bg-purple-700 text-white rounded-lg
                                 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Upload className="w-4 h-4" />
                        {isSubmitting ? 'Posting...' : 'Post Article'}
                    </button>
                </div>

                <div className="relative">
                    <input
                        type="file"
                        id="image-upload"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                    />
                    <label
                        htmlFor="image-upload"
                        className={`block w-full h-full min-h-[300px] border-2 border-dashed rounded-lg 
                                  cursor-pointer ${imagePreview ? 'border-transparent' : 'border-gray-300'}
                                  hover:bg-gray-50 transition-colors`}
                    >
                        {imagePreview ? (
                            <div className="relative w-full h-full">
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full h-full object-cover rounded-lg"
                                />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center
                                              opacity-0 hover:opacity-100 transition-opacity">
                                    <p className="text-white flex items-center">
                                        <Image className="w-5 h-5 mr-2" />
                                        Change Image
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full">
                                <Image className="w-8 h-8 text-gray-400 mb-2" />
                                <p className="text-sm text-gray-500">Click to upload an image</p>
                            </div>
                        )}
                    </label>
                </div>
            </div>
        </form>
    );
};


const NewsPage = () => {
    const { isAdmin, logout } = useAuth();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [articles, setArticles] = useState([]);
    const [events, setEvents] = useState([]); // Add events state
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false); // Optional: To toggle calendar view

    useEffect(() => {
        loadArticles();
        loadEvents(); // Load events when component mounts
    }, []);

    const loadEvents = async () => {
        try {
            const fetchedEvents = await eventUtils.getEvents();
            setEvents(fetchedEvents);
        } catch (error) {
            console.error('Error loading events:', error);
            // Optionally set an error state for events
        }
    };

    const handleEventAdd = async (eventData) => {
        try {
            await eventUtils.createEvent(eventData);
            await loadEvents(); // Reload events after adding
        } catch (error) {
            console.error('Error adding event:', error);
            alert('Failed to add event');
            throw error;
        }
    };

    const handleEventDelete = async (eventId) => {
        try {
            await eventUtils.deleteEvent(eventId);
            await loadEvents(); // Reload events after deletion
        } catch (error) {
            console.error('Error deleting event:', error);
            alert('Failed to delete event');
        }
    };

    const handleArticleClick = (article) => {
        setSelectedArticle(article);
    };

    const loadArticles = async () => {
        try {
            setLoading(true);
            setError(null);
            const fetchedArticles = await articleUtils.getArticles();
            const filteredArticles = fetchedArticles.filter(article =>
                !EXCLUDED_CATEGORIES.includes(article.category)
            );
            setArticles(filteredArticles);
        } catch (error) {
            console.error('Error loading articles:', error);
            setError(error.message || 'Failed to load articles');
        } finally {
            setLoading(false);
        }
    };

    const handleLoginSuccess = () => {
        setShowLoginModal(false);
    };

    const handleLogout = () => logout();

    const handleArticleSubmit = async (formData) => {
        try {
            if (!formData.title || !formData.content) {
                throw new Error('Title and content are required');
            }

            if (formData.image) {
                const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                if (!validTypes.includes(formData.image.type)) {
                    throw new Error('Invalid image type. Please upload a JPG, PNG, GIF, or WEBP file.');
                }
                if (formData.image.size > 10 * 1024 * 1024) {
                    throw new Error('Image file is too large. Maximum size is 10MB.');
                }
            }

            const response = await articleUtils.createArticle({
                title: formData.title,
                content: formData.content,
                imageFile: formData.image,
                type: 'article'
            });

            await loadArticles();
            return response;
        } catch (error) {
            console.error('Error in handleArticleSubmit:', error);
            alert(error.message || 'Failed to create article');
            throw error;
        }
    };

    const handleArticleDelete = async (articleId, imageId) => {
        if (window.confirm('Are you sure you want to delete this article?')) {
            try {
                await articleUtils.deleteArticle(articleId, imageId);
                await loadArticles(); // Refresh the articles list
            } catch (error) {
                console.error('Error deleting article:', error);
                alert(error.message || 'Failed to delete article');
            }
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pb-24 bg-gray-50">
            {/* Page header */}
            <header className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-purple-950 pt-32 pb-20">
                <div className="pointer-events-none absolute inset-0">
                    <div className="absolute -top-10 left-1/4 h-72 w-72 rounded-full bg-purple-500/20 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 h-64 w-64 rounded-full bg-purple-300/10 blur-3xl" />
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <span className="inline-block mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-purple-200">
                        Community Charitable Ministry
                    </span>
                    <h1 className="font-display text-5xl sm:text-6xl font-semibold text-white">
                        Latest{' '}
                        <span className="bg-gradient-to-r from-purple-200 to-white bg-clip-text italic text-transparent">
                            News
                        </span>
                    </h1>
                    <p className="mx-auto mt-5 max-w-2xl text-lg text-purple-100/80">
                        Stories, updates, and milestones from across our programs and the partners
                        who make them possible.
                    </p>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative">
                {error && (
                    <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* News Section */}
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {articles.length > 0 ? (
                            articles.map(article => (
                                <NewsArticle
                                    key={article.$id}
                                    article={article}
                                    isAdmin={isAdmin}
                                    onDelete={handleArticleDelete}
                                    onClick={handleArticleClick}
                                />
                            ))
                        ) : (
                            <div className="col-span-full text-center bg-white rounded-xl p-12">
                                <FileText className="mx-auto w-12 h-12 text-gray-400 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    No Articles Yet
                                </h3>
                                <p className="text-gray-600">
                                    Check back soon for new updates.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* Admin Panel */}
            <AdminPanel
                isAdmin={isAdmin}
                onLogout={handleLogout}
                onSubmit={handleArticleSubmit}
            />

            {/* Admin Login Button */}
            {!isAdmin && (
                <button
                    onClick={() => setShowLoginModal(true)}
                    className="fixed bottom-6 right-6 w-10 h-10 bg-white/90 backdrop-blur rounded-full
                             shadow-lg hover:shadow-xl border border-gray-200/50 flex items-center
                             justify-center transition-all duration-200 hover:-translate-y-0.5 z-50"
                    aria-label="Admin Login"
                >
                    <Lock className="w-4 h-4 text-gray-600"/>
                </button>
            )}

            {/* Login Modal */}
            {showLoginModal && (
                <AdminLogin onClose={() => setShowLoginModal(false)} />
            )}

            {/* Article Overlay */}
            {selectedArticle && (
                <ArticleOverlay
                    article={selectedArticle}
                    onClose={() => setSelectedArticle(null)}
                />
            )}
        </div>
    );
};

export default NewsPage;
